import useTranslation from 'next-translate/useTranslation'
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  toaster,
  Notification,
  FlexboxGrid,
} from 'rsuite'
import { useRouter } from 'next/router'
import { useFeatureValue } from '@growthbook/growthbook-react'

import usePrice from '../../services/usePrice'
import {
  getTotalProductQuantity,
  getProductVariantQuantities,
  getIsOverExactStock,
  hasStockBackDate,
  getDeliveryEstimation,
  getProductCurrency,
  getHasBulkBuyableCategory,
} from '../../lib/product'
import { ALERT_DURATION, ScreenSize } from '../../utils/constants'
import { formatPriceNumber } from '../../utils/math'
import { Product, ProductQuantity, Supplier } from '../../utils/types/Product'
import orderRequestManualCheckLimitEvent from '../../utils/analytics/orderRequestManualCheckLimitEvent'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import ProductVariantSelector from '../ProductVariantSelector'
import useRFQModal from '../Modals/hooks/useRFQModal'
import useIsOnMobile from '../../services/useIsOnMobile'
import { isElementVisible } from '../../utils/scroll'
import { useCart } from '../Cart/CartProvider'
import { useAuth } from '../../services/useAuth'
import { getSupplierMovData } from '../../lib/cart'
import { isSuperUser } from '../../lib/supplier'
import useEcomEvents from '../../services/analytics/useEcomEvents'
import { isOrderPriceLimitReached } from '../../lib/resources/price'
import ProductPriceSection from './ProductPriceSection'
import PackagingDetailsSPP from './PackagingDetailsSPP'
import DiscountTableSPP from './DiscountTableSPP'
import AddProductButtonsSPP from './AddProductButtonsSPP'
import { useCyModelWithProps } from '../../services/useCyModel'
import useRecentlyViewedProducts from '../../services/useRecentlyViewedProducts'
import ProductCarousel from '../Carousels/ProductCarousel'
import { AnalyticsProductCarouselTypes } from '../../utils/types/analytics'
import useOthersYouMayLike from '../../services/recommendationsAPI/useOthersYouMayLike'
import ShippingIconBorderless from '../Icons/ShippingIconBorderless'
import { getSupplierDeliveryDetails, isFreeDelivery } from '../../lib/service'
import ProductColorVariants from '../Product/ProductColorVariants'
import ChatPanel from '../ChatPanel'
import useVariantUI from '../../services/useVariantUI'
import VariantQuantityButtonWrapper from '../VariantQuantityButtonWrapper'
import useSelectedVariant from '../hooks/useSelectedVariant'
import DownArrowsIcon from '../Icons/DownArrowsIcon'
import { ProductVariantsRef } from '../types/ProductVariantsRef'
import useWhenVisible from '../../services/useWhenVisible'

import buttonStyles from '../../styles/CustomButtons.module.less'
import styles from '../../styles/ProductColumn.module.less'

interface ProductColumnProps {
  product: Product
  supplier?: Supplier
  productQuantity: ProductQuantity[]
  toggleOrderModal: (val: boolean) => void
  setProductQuantity: Dispatch<SetStateAction<ProductQuantity[]>>
  addToCartLink: string
  className?: string
  isFavoriteProduct?: boolean
}

const ProductColumn: FC<ProductColumnProps> = (props) => {
  const {
    product,
    supplier,
    productQuantity,
    addToCartLink,
    className,
    setProductQuantity,
    toggleOrderModal,
  } = props

  const [isLoading, setIsLoading] = useState(false)
  // Triggered when product added to cart and MOQ is not met
  const [isMoqWarningActive, setIsMoqWarningActive] = useState(false)
  // Track if the add to cart button is at the bottom of the screen
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [showDownArrows, setShowDownArrows] = useState(true)

  const { addProduct, refresh } = useCart()
  const { locale: localeAndCountryString, isReady: isRouterReady } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(localeAndCountryString)
  const { user, isVatIncluded } = useAuth()
  const enableRelatedProducts = useFeatureValue('enable-related-products', false)

  const {
    data: othersYouMayLike,
    isLoading: isOthersYouMayLikeLoading,
    triggerDetailPageView,
  } = useOthersYouMayLike({ pageSize: 20 })

  const {
    data: recentlyViewedProducts,
    isLoading: isRecentlyViewedProductsLoading,
  } = useRecentlyViewedProducts()

  // Remove current SPP from recently viewed products
  const recentlyViewedProductsFiltered = recentlyViewedProducts?.filter((prod) => prod['product.code'] !== product['product.code'])

  const totalQuantity = getTotalProductQuantity(productQuantity)
  const {
    default: {
      price: defaultPrice,
    },
    final: {
      price: finalProductPrice,
      total: finalTotalPrice,
    },
    moq,
  } = usePrice(product, totalQuantity)

  const {
    selectedVariantId,
    selectedVariant,
    handleSelectVariant,
  } = useSelectedVariant({
    productQuantity,
    setProductQuantity,
    moq,
  })

  const variantUI = useVariantUI()

  const { t } = useTranslation('products')
  const isOnMobile = useIsOnMobile(ScreenSize.md)

  const productVariantsRef = useRef<ProductVariantsRef>(null)
  const addToCartWrapperRef = useRef<HTMLDivElement>(null)
  const minimumTextRef = useRef<HTMLDivElement>(null)

  const analyticsLocation = 'single_product'
  const productVariants = getProductVariantQuantities(productQuantity)
  const currency = getProductCurrency(product)
  const isFreeDeliveryAvailable = isFreeDelivery(product, country, isVatIncluded)
  const hasSingleVariant = productQuantity.length === 1
  const hasBulkBuyableCategory = getHasBulkBuyableCategory(product)

  // Boolean flags for conditional rendering logic
  const shouldShowVariantSelector = !hasSingleVariant
  const shouldShowMobileStickyButton = !hasSingleVariant && (variantUI === 'list' || hasBulkBuyableCategory)
  const shouldShowMobileQuantityWrapper = hasSingleVariant || (variantUI !== 'list' && !hasBulkBuyableCategory)

  const showChooseQuantityHeader = () => {
    const chooseQuantityElementRef = productVariantsRef.current?.chooseQuantityRef
    const isChooseQuantityVisible = isElementVisible(chooseQuantityElementRef?.current)

    if (!isChooseQuantityVisible) {
      productVariantsRef.current?.chooseQuantityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const openRFQModal = useRFQModal({
    analyticsLocation,
    product,
  })

  const productPrice = formatPriceNumber(finalProductPrice)
  const isOverExactStock = productQuantity.every(
    (ele) => getIsOverExactStock(ele)
    || (ele.exactStockLevel !== null && Number(ele.exactStockLevel) <= 0),
  )

  const { addToCartEvent, addToCartUnderMOQEvent } = useEcomEvents()

  // Allowing super user to add product to cart even if order amount greater than max price limit
  const isProductOrderLimitReached = !isSuperUser(user) && isOrderPriceLimitReached(
    finalTotalPrice, product,
  )

  const hasAddedQuantityWithoutSbd = productQuantity
    .some((ele) => ele.exactStockLevel !== null
    && Number(ele.quantity) > Number(ele.exactStockLevel)
    && !hasStockBackDate(ele.stockDate))

  const handleAddToCart = async () => {
    if (!supplier) { return } // Don't do anything until supplier data has loaded

    const isUnderMoq = moq > Number(totalQuantity)
      || Number.isNaN(Number(totalQuantity))
      || Number.isNaN(productPrice)

    // Faulty add to cart – product amount is below MOQ
    if (isUnderMoq) {
      showChooseQuantityHeader()

      // Analytics event
      if (!isMoqWarningActive) {
        addToCartUnderMOQEvent({ productsData: [{ product, productVariants }] })
      }
      setIsMoqWarningActive(true)
      return
    }

    const cartData = productQuantity.map((prodQuantity) => ({
      id: prodQuantity.productId,
      quantity: Number(prodQuantity.quantity),
      supplierId: supplier.id,
      variant: prodQuantity.variantId,
    }))

    setIsLoading(true)
    const { error } = await addProduct(addToCartLink, cartData)

    setIsLoading(false)

    if (error) {
      toaster.push((
        <Notification
          type="info"
          header={t('common:Oh no!')}
          duration={ALERT_DURATION}
          closable
        >
          <p>{`${t('cart:Something went wrong, unable to add product to cart')}.`}</p>
        </Notification>
      ),
      { placement: 'topEnd' })
    } else {
      setIsMoqWarningActive(false)

      // Analytics event
      // Get augmented cart with product and supplier data for correct isUnderMov value
      const { modifiedCart } = (await refresh([supplier], [product])) || {}
      const {
        isUnderMov,
      } = getSupplierMovData(supplier['supplier.code'], country, modifiedCart, isVatIncluded)

      // Reset selected quantity after add to cart
      setProductQuantity((prevState) => prevState.map((quantity) => ({
        ...quantity,
        quantity: 0,
      })))
      addToCartEvent({ modifiedCart, productsData: [{ product, productVariants, isUnderMov }] })
    }
  }

  const isRFQProduct = !defaultPrice

  useEffect(() => {
    triggerDetailPageView([product['product.code']])
  }, [product['product.code']])

  const onAddToCartClick = useCallback(
    !isProductOrderLimitReached && !isOverExactStock
      ? handleAddToCart
      : () => {
      // Analytics event
        orderRequestManualCheckLimitEvent(product, totalQuantity)
        toggleOrderModal(true)
      },
    [
      isProductOrderLimitReached,
      isOverExactStock,
      hasAddedQuantityWithoutSbd,
      handleAddToCart,
    ],
  )

  useCyModelWithProps('SingleProduct', {
    openRFQModal,
    handleAddToCart,
    productColumn: {
      deliveryEstimation: getDeliveryEstimation(product, country, locale),
    },
  })

  const {
    freeShippingLimit,
    deliveryCost,
  } = getSupplierDeliveryDetails(supplier, country, isVatIncluded)

  const deliveryDetailsDeliveryDate = (
    <FlexboxGrid
      data-testid="product-delivery-text"
      className={`flex-flow-row ${styles['estimated-delivery-date']}`}
    >
      <ShippingIconBorderless />
      {`${t('products:Estimated delivery')}: ${getDeliveryEstimation(product, country, locale)}` }
    </FlexboxGrid>
  )

  const deliveryDetailsDeliveryFee = (
    <p className="margin-top-spacer-double">
      {isFreeDeliveryAvailable
        ? (
          <b>
            {t('products:Free delivery')}
          </b>
        )
        : t(
          'products:Shipping fee is {{ shippingFee }} {{ currency }} for orders under {{ amount }} {{ currency }}',
          {
            shippingFee: formatPriceNumber(deliveryCost),
            currency,
            amount: formatPriceNumber(freeShippingLimit),
          },
        )}
    </p>
  )

  // Function to check if the cart is at the bottom
  const checkIsAtBottom = useCallback((_: Event, cartHeight?: number) => {
    if (isOnMobile || !addToCartWrapperRef.current || !minimumTextRef.current) return

    const cartRect = addToCartWrapperRef.current.getBoundingClientRect()
    const minTextRect = minimumTextRef.current.getBoundingClientRect()
    // If the minimum text is visible (above the fold), remove shadow
    setIsAtBottom((minTextRect.top - (cartHeight || cartRect.height)) <= window.innerHeight)
  }, [isOnMobile])

  // Setup scroll listener
  useWhenVisible(() => {
    if (isOnMobile) return undefined

    window.addEventListener('scroll', checkIsAtBottom)
    // Initial check, give custom height to cart height to avoid shadow
    checkIsAtBottom(new Event('scroll'))

    return () => {
      window.removeEventListener('scroll', checkIsAtBottom)
    }
  }, addToCartWrapperRef, [isOnMobile, checkIsAtBottom])

  // Hide down arrows icon after 9 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDownArrows(false)
    }, 9000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div className={`${className} ${styles['product-column-right']}`}>
      {isOnMobile
        ? (
          <>
            {shouldShowMobileStickyButton && (
              <AddProductButtonsSPP
                isRFQProduct={isRFQProduct}
                isLoading={isLoading}
                finalTotalPrice={finalTotalPrice}
                product={product}
                isOverExactStock={isOverExactStock}
                onAddToCartClick={onAddToCartClick}
                className={styles['add-to-cart-mobile-sticky']}
              />
            )}

            <div className={styles['product-info']}>
              <ProductPriceSection
                product={product}
                productQuantity={productQuantity}
              />
            </div>

            <p className="margin-top-spacer-double">{deliveryDetailsDeliveryDate}</p>

            {enableRelatedProducts && (
              <ProductColorVariants
                key={product.id}
                product={product}
              />
            )}

            {shouldShowVariantSelector && (
              <div className={isRFQProduct && !isOnMobile ? '' : styles['product-selection-section']}>
                <ProductVariantSelector
                  ref={productVariantsRef}
                  product={product}
                  productQuantity={productQuantity}
                  setProductQuantity={setProductQuantity}
                  isMoqWarningActive={isMoqWarningActive}
                  setIsMoqWarningActive={setIsMoqWarningActive}
                  selectedVariantId={selectedVariantId}
                  onSelectVariant={handleSelectVariant}
                />
              </div>
            )}
            {shouldShowMobileQuantityWrapper && (
              <div className="margin-top-spacer-double">
                <VariantQuantityButtonWrapper
                  product={product}
                  productQuantity={productQuantity}
                  setProductQuantity={setProductQuantity}
                  isMoqWarningActive={isMoqWarningActive}
                  setIsMoqWarningActive={setIsMoqWarningActive}
                  selectedVariant={selectedVariant}
                  fullWidth
                  onAddToCartClick={onAddToCartClick}
                  renderAddToCartButton={() => (
                    <AddProductButtonsSPP
                      isRFQProduct={isRFQProduct}
                      isLoading={isLoading}
                      finalTotalPrice={finalTotalPrice}
                      isOverExactStock={isOverExactStock}
                      product={product}
                      onAddToCartClick={onAddToCartClick}
                    />
                  )}
                />
              </div>
            )}

            {deliveryDetailsDeliveryFee}

            <PackagingDetailsSPP product={product} />

            <FlexboxGrid
              className="margin-top-spacer-tripple"
              align="middle"
            >
              <FlexboxGrid.Item>
                <p className={styles['rfq-text']}>
                  {t('Need larger quantities')}
                </p>
              </FlexboxGrid.Item>

              <Button
                data-testid="request-quote-button"
                disabled={!isRouterReady}
                onClick={() => openRFQModal()}
                className={`
                            padding-zero
                            ${buttonStyles['custom-button']}
                            ${buttonStyles['button-tertiary']}
                            ${buttonStyles['button-transparent']}
                            ${buttonStyles['button-caret']}
                            `}
                type="button"
              >
                <b className={styles['rfq-btn']}>{t('Request a custom quote')}</b>
              </Button>
            </FlexboxGrid>

            {!isRFQProduct && <DiscountTableSPP product={product} />}

            <ProductCarousel
              carouselType={AnalyticsProductCarouselTypes.OthersYouMayLike}
              carouselGap={5}
              productCardType="simple"
              title={t('products:Other products you may like')}
              titleSpacerClassName="margin-bottom-spacer"
              products={othersYouMayLike}
              isLoading={isOthersYouMayLikeLoading}
              className="margin-top-spacer-tripple"
              hasCardBorder={false}
              hideArrows={Boolean(othersYouMayLike?.length && othersYouMayLike.length < 3)}
            />
            <ProductCarousel
              testId="recently-viewed-products"
              carouselType={AnalyticsProductCarouselTypes.RecentlyViewed}
              carouselGap={5}
              productCardType="simple"
              className="margin-top-spacer-tripple"
              title={t('products:Recently viewed')}
              titleSpacerClassName="margin-bottom-spacer"
              products={recentlyViewedProductsFiltered}
              hasCardBorder={false}
              hideArrows={
                Boolean(recentlyViewedProducts?.length
                && recentlyViewedProducts.length < 3)
              }
              hide={recentlyViewedProductsFiltered?.length === 0
                        && !isRecentlyViewedProductsLoading}
            />
            <ChatPanel
              data-testid="product-contact-card"
              customTextContent={t('modals:Get help from our experts')}
              title={t('modals:Need help?')}
              className="margin-top-spacer-double"
            />
          </>
        )
        : (
          <>
            <div className="padding-right-spacer-double padding-left-spacer-double">
              <div className={styles['product-info']}>
                <ProductPriceSection
                  product={product}
                  productQuantity={productQuantity}
                />
              </div>

              {deliveryDetailsDeliveryDate}
            </div>
            <div className="padding-left-spacer-double">
              {enableRelatedProducts && <ProductColorVariants product={product} />}
            </div>

            <div className={styles['product-selection-section']}>
              {shouldShowVariantSelector && (
                <div className="margin-bottom-spacer-double">
                  <ProductVariantSelector
                    ref={productVariantsRef}
                    product={product}
                    productQuantity={productQuantity}
                    setProductQuantity={setProductQuantity}
                    isMoqWarningActive={isMoqWarningActive}
                    setIsMoqWarningActive={setIsMoqWarningActive}
                    selectedVariantId={selectedVariantId}
                    onSelectVariant={handleSelectVariant}
                  />
                </div>
              )}
              {shouldShowMobileStickyButton ? (
                <div
                  ref={addToCartWrapperRef}
                  data-testid="checkout-section-wrapper"
                  className={`${styles['checkout-section-wrapper']} ${isAtBottom ? styles['no-shadow'] : ''}`}
                >
                  {!isAtBottom && showDownArrows && (
                    <div className={styles['down-arrows-icon']}>
                      <DownArrowsIcon />
                    </div>
                  )}
                  <AddProductButtonsSPP
                    isRFQProduct={isRFQProduct}
                    isLoading={isLoading}
                    finalTotalPrice={finalTotalPrice}
                    isOverExactStock={isOverExactStock}
                    product={product}
                    onAddToCartClick={onAddToCartClick}
                  />
                </div>
              ) : (
                <VariantQuantityButtonWrapper
                  product={product}
                  productQuantity={productQuantity}
                  setProductQuantity={setProductQuantity}
                  isMoqWarningActive={isMoqWarningActive}
                  setIsMoqWarningActive={setIsMoqWarningActive}
                  selectedVariant={selectedVariant}
                  onAddToCartClick={onAddToCartClick}
                  renderAddToCartButton={() => (
                    <AddProductButtonsSPP
                      isRFQProduct={isRFQProduct}
                      isLoading={isLoading}
                      finalTotalPrice={finalTotalPrice}
                      isOverExactStock={isOverExactStock}
                      product={product}
                      onAddToCartClick={onAddToCartClick}
                    />
                  )}
                />
              )}
              {deliveryDetailsDeliveryFee}

              <PackagingDetailsSPP product={product} />

              <div ref={minimumTextRef}>
                <FlexboxGrid
                  className="margin-top-spacer-tripple"
                  align="middle"
                >
                  <FlexboxGrid.Item>
                    <p className={styles['rfq-text']}>
                      {t('Need larger quantities')}
                    </p>
                  </FlexboxGrid.Item>
                  <Button
                    data-testid="request-quote-button"
                    disabled={!isRouterReady}
                    onClick={() => openRFQModal()}
                    className={`
                            padding-zero
                            ${buttonStyles['custom-button']}
                            ${buttonStyles['button-tertiary']}
                            ${buttonStyles['button-transparent']}
                            ${buttonStyles['button-caret']}
                            `}
                    type="button"
                  >
                    <b className={styles['rfq-btn']}>{t('Request a custom quote')}</b>
                  </Button>
                </FlexboxGrid>
              </div>

              {!isRFQProduct && <DiscountTableSPP product={product} />}

              <ProductCarousel
                carouselType={AnalyticsProductCarouselTypes.OthersYouMayLike}
                productCardType="simple"
                title={t('products:Other products you may like')}
                titleSpacerClassName="margin-bottom-spacer"
                products={othersYouMayLike}
                isLoading={isOthersYouMayLikeLoading}
                className="margin-top-spacer-tripple"
                hasCardBorder={false}
                hideArrows={Boolean(othersYouMayLike?.length && othersYouMayLike.length < 3)}
              />
              <ProductCarousel
                testId="recently-viewed-products"
                carouselType={AnalyticsProductCarouselTypes.RecentlyViewed}
                productCardType="simple"
                className="margin-top-spacer-tripple"
                title={t('products:Recently viewed')}
                titleSpacerClassName="margin-bottom-spacer"
                products={recentlyViewedProductsFiltered}
                hasCardBorder={false}
                hideArrows={Boolean(
                  recentlyViewedProducts?.length
                    && recentlyViewedProducts.length < 3,
                )}
                hide={
                  !recentlyViewedProductsFiltered?.length && !isRecentlyViewedProductsLoading
                  }
              />

              <ChatPanel
                data-testid="product-contact-card"
                customTextContent={t('modals:Get help from our experts')}
                title={t('modals:Need help?')}
                className="margin-top-spacer-double"
              />
            </div>
          </>
        )}
    </div>
  )
}

export default ProductColumn
